/**
 * Regras da aba "Relatório" da Composição de Saldo.
 *
 * Pedido do usuário (02/10/2026): relatório "tanto de todas as empresas,
 * quanto de empresa ou grupo de empresa filtradas, trazendo a movimentação
 * por período filtrado, o valor de saldo em aberto, o valor já pago, as
 * contas analíticas com mais movimentação, as com mais saldo em aberto".
 *
 * Tudo aqui é função pura: o componente só renderiza. É o que permite travar
 * as regras de leitura por teste, sem montar a tela.
 */

import { IRelatorioComposicao, IRelatorioLinha } from '../../../auditoriaInteligente/types';

/** Formata moeda no padrão brasileiro, sem o prefixo (a tela põe o "R$"). */
export const fmtValor = (n: number | string | null | undefined): string => {
  const v = Number(n ?? 0);
  if (!Number.isFinite(v)) return '—';
  return v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/** Inteiro com separador de milhar. */
export const fmtInt = (n: number | null | undefined): string => {
  const v = Number(n ?? 0);
  if (!Number.isFinite(v)) return '—';
  return v.toLocaleString('pt-BR');
};

/**
 * Rótulo do tipo de item. O texto importa: "pagamento a maior" NÃO é
 * "já pago" — é baixa que não achou provisão, e o contador precisa explicar.
 */
export const ROTULO_TIPO: Record<string, string> = {
  provisao: 'Em aberto',
  pagamento_maior: 'Pagamento sem título',
  nao_identificado: 'Não identificado',
};

/**
 * O recorte vira o parâmetro `empresas` da rota.
 *
 * Lista VAZIA devolve `undefined`, não string vazia: a rota entende ausência
 * como "carteira inteira", e `?empresas=` seria ambíguo.
 */
export const paramEmpresas = (ids: number[]): string | undefined =>
  ids.length ? ids.join(',') : undefined;

/**
 * O relatório tem dado para mostrar?
 *
 * `data_posicao` nula significa que o lote nunca rodou neste recorte — e a
 * tela mostra o aviso do backend, não uma página de zeros. Zero afirmaria
 * "sem pendência", que é afirmação diferente de "não calculei".
 */
export const temDados = (r: IRelatorioComposicao | undefined): boolean =>
  !!r && r.data_posicao != null && !!r.totais;

/**
 * Quantos por cento das contas do recorte não têm saldo de abertura.
 *
 * Sem abertura, a composição não sabe o que vinha de antes da base e o
 * "em aberto" pode estar incompleto: o item antigo some da lista porque sua
 * provisão nunca foi carregada. Medido em 05/10: 1.298 de 1.326 contas.
 */
export const pctSemAbertura = (r: IRelatorioComposicao | undefined): number => {
  const t = r?.totais;
  if (!t || !t.contas) return 0;
  return Math.round((Number(t.sem_abertura ?? 0) / Number(t.contas)) * 100);
};

/**
 * A ressalva do saldo invertido, em texto, SEPARANDO as duas causas.
 *
 * Das 731 contas invertidas medidas em 05/10, 440 se explicam pelo RECORTE:
 * o fato gerador está antes da janela consultada. Caso real investigado
 * (856/1061, Adiantamento Acoord): debitado em 2024, baixado em 01/2026 — o
 * ERP mostra +359.787,85 e a composição −359.787,85, os DOIS certos, medindo
 * grandezas diferentes.
 *
 * Um aviso só, com os 731, transformaria 291 achados úteis em 731 falsos
 * alarmes e treinaria o contador a ignorar o aviso.
 */
export const textoInvertidos = (r: IRelatorioComposicao | undefined): string | null => {
  const inv = r?.saldos_invertidos;
  if (!inv || !inv.total) return null;
  const aInvestigar = Number(inv.a_investigar ?? 0);
  const porRecorte = Number(inv.por_recorte ?? 0);
  if (!aInvestigar) {
    return (
      `${fmtInt(porRecorte)} conta(s) com saldo contrário à natureza, todas ` +
      'explicadas pelo recorte: o fato gerador está antes do período consultado. ' +
      'Nenhuma precisa de investigação.'
    );
  }
  const base =
    `${fmtInt(aInvestigar)} conta(s) com saldo contrário à natureza SEM explicação ` +
    'pelo saldo de abertura — essas merecem conferência.';
  if (!porRecorte) return base;
  return (
    `${base} Outras ${fmtInt(porRecorte)} também estão invertidas, mas porque o fato ` +
    'gerador antecede o período: não são erro contábil.'
  );
};

/**
 * A ressalva da ITG 2000 (R1), item 24, quando o recorte soma
 * estabelecimentos do MESMO grupo.
 *
 * Devolve null quando não há o que ressalvar — uma empresa só, ou empresas de
 * grupos diferentes. Aviso genérico treina o contador a ignorar os avisos.
 */
export const textoSomaSimples = (r: IRelatorioComposicao | undefined): string | null => {
  const s = r?.soma_simples;
  if (!s) return null;
  const n = s.reciprocas?.length ?? 0;
  const inicio =
    'Soma simples dos estabelecimentos marcados: nada foi eliminado. Pela ' +
    'ITG 2000 (R1), item 24, as contas recíprocas entre matriz e filiais ' +
    '(conta corrente, transferência de estoque) devem ser ELIMINADAS nas ' +
    'demonstrações — aqui elas continuam no total. Não é o consolidado contábil.';
  if (!n) return inicio;
  const semSaldo = Number(s.reciprocas_sem_saldo ?? 0);
  // O aviso de saldo desconhecido existe porque "zero" e "não sei" são
  // afirmações diferentes: a soma abaixo está incompleta, e esconder isso
  // faria o contador confiar num total parcial.
  const ressalva = semSaldo
    ? ` ${fmtInt(semSaldo)} dessas está(ão) sem saldo apurado nesta data, então a soma é parcial.`
    : '';
  return (
    `${inicio} ${fmtInt(n)} conta(s) recíproca(s) somando R$ ${fmtValor(
      s.soma_reciprocas,
    )} em módulo.${ressalva} As linhas abaixo trazem qual conta e de qual empresa — ` +
    'confira antes de usar o total.'
  );
};

/**
 * Ordena a série mensal e devolve só os meses COM item.
 *
 * Mês sem item em aberto não é "mês sem movimento": é mês cujos títulos já
 * foram todos baixados, e o motor os retirou da lista. Plotar o zero sugeriria
 * ausência de operação.
 */
export const serieParaGrafico = (
  r: IRelatorioComposicao | undefined,
): { mes: string; itens: number; valor: number }[] => {
  const serie = r?.serie_mensal ?? [];
  return serie
    .filter(x => Number(x.itens ?? 0) > 0)
    .map(x => ({
      mes: String(x.mes ?? ''),
      itens: Number(x.itens ?? 0),
      valor: Number(x.valor_aberto ?? 0),
    }))
    .sort((a, b) => a.mes.localeCompare(b.mes));
};

/** Maior valor da série — base da escala das barras. Nunca zero (divisão). */
export const escalaDaSerie = (pontos: { itens: number }[]): number =>
  Math.max(1, ...pontos.map(p => p.itens));

/**
 * Rótulo da conta na lista: "[177] Fornecedores Nacionais".
 *
 * O código entra entre colchetes porque é ele que o contador digita no ERP —
 * o nome sozinho não localiza a conta, já que o plano é por empresa.
 */
export const rotuloConta = (l: IRelatorioLinha): string => {
  const cod = l.conta ?? '—';
  const nome = l.conta_nome ?? '—';
  return `[${cod}] ${nome}`;
};

/**
 * A linha tem saldo conhecido?
 *
 * `null` em `saldo_razao` significa "não há linha de saldo nesta data", que é
 * diferente de zero. A tela mostra "—", nunca "0,00": zero afirmaria conta
 * zerada.
 */
export const temSaldo = (l: { saldo_razao?: number | null }): boolean => l.saldo_razao != null;

/* ------------------------------------------------- por empresa e por grupo */

/**
 * O percentual formatado, ou "—" quando nao ha' base.
 *
 * `null` do backend significa "nao ha' o que medir" (conta sem movimento, ou
 * cujo movimento inteiro e' sem par por natureza). Mostrar "0%" ali seria
 * acusar o motor de nao ter casado algo que nao tem par.
 */
export const fmtPct = (n: number | null | undefined): string =>
  n == null || !Number.isFinite(Number(n)) ? '—' : `${Number(n).toFixed(1)}%`;

/**
 * A cor do percentual. Os cortes nao sao estetica: 70% e' a faixa em que a
 * carteira ja' opera (medido em 522 empresas: 86,1% agregado, e a pior
 * empresa grande em 54,9%), entao abaixo de 50% e' o que realmente pede
 * investigacao.
 */
export const corDoPct = (n: number | null | undefined, baseDesproporcional = false): string => {
  if (n == null) return 'gray.400';
  // Base irrisoria: o numero e' verdadeiro, mas mede o RECORTE (o fato
  // gerador esta' fora da janela), nao a conciliacao. Pintar de vermelho
  // acusaria o motor de nao casar algo que nunca entrou no periodo —
  // medido em 09/10/2026 na empresa 1142: R$ 1.795,90 de provisionado
  // contra R$ 73,9 mi de sem par, que sao os lancamentos-bloco
  // "RECEBIMENTOS 2025" e "PAGAMENTOS 2025".
  if (baseDesproporcional) return 'gray.400';
  if (n >= 80) return 'green.400';
  if (n >= 50) return 'yellow.400';
  return 'red.400';
};

/**
 * O texto que explica o percentual neutro. Vive aqui, e nao no JSX, para
 * ser testavel: e' a frase que decide se o contador investiga a conciliacao
 * ou pede a carga do exercicio anterior.
 */
export const AJUDA_BASE_DESPROPORCIONAL =
  "O provisionado na janela e' irrisorio diante do sem-par: este " +
  'percentual mede o RECORTE, nao a conciliacao. Em geral e' +
  "' transporte de saldo do exercicio anterior, cujo fato gerador esta' " +
  'fora do periodo analisado.';

/**
 * `true` quando alguma linha VISIVEL tem base desproporcional — e so' entao a
 * legenda do asterisco aparece.
 *
 * A regua e' o que esta' na tela, nao a lista inteira: a tabela corta em
 * `limite` linhas ate' o "ver todas", e uma legenda sem asterisco visivel
 * manda o leitor procurar uma marca que nao esta' ali.
 */
export const visiveisTemDesproporcional = (
  linhas: { base_desproporcional?: boolean }[] | undefined,
  verTodas: boolean,
  limite: number,
): boolean => {
  const ls = linhas ?? [];
  return (verTodas ? ls : ls.slice(0, limite)).some(l => !!l.base_desproporcional);
};

/**
 * `true` quando vale mostrar a coluna "nao tem par" — isto e', quando algum
 * recorte tem valor ali.
 *
 * Sem esta checagem a tela ganharia uma coluna de zeros nos dias em que o job
 * rodou ANTES do deploy dos rotulos (o lote e' das 03:00), e uma coluna
 * zerada nao distingue "nao ha'" de "nao foi calculado".
 */
export const temSemParNatureza = (
  linhas: { sem_par_natureza?: number | null }[] | undefined,
): boolean => (linhas ?? []).some(l => Number(l.sem_par_natureza ?? 0) > 0);

/**
 * `true` quando o relatorio foi calculado ANTES de os rotulos de natureza
 * existirem — e a tela precisa dizer isso, senao o contador le' "0 sem par
 * por natureza" como fato.
 *
 * A heuristica e' honesta: se ha' orfas mas NENHUMA classificada, ou o lote e'
 * antigo ou de fato nenhuma se classifica. Nos dois casos avisar e' melhor que
 * calar, porque o numero muda depois do proximo lote.
 */
export const loteAntesDosRotulos = (
  linhas: { orfas?: number | null; sem_par_natureza?: number | null }[] | undefined,
): boolean => {
  const ls = linhas ?? [];
  if (!ls.length) return false;
  const temOrfas = ls.some(l => Number(l.orfas ?? 0) > 0);
  return temOrfas && !temSemParNatureza(ls);
};

/** Quantos titulos do recorte tem pagamento ligado, em texto. */
export const textoLigacoes = (l: {
  titulos_ligados?: number | null;
  titulos?: number | null;
}): string => {
  const total = Number(l.titulos ?? 0);
  if (!total) return '—';
  return `${fmtInt(l.titulos_ligados)} de ${fmtInt(total)}`;
};

/**
 * O nome que a linha de grupo exibe.
 *
 * Com mais de um estabelecimento o nome de UMA empresa seria enganoso, entao
 * usa a raiz do CNPJ como identidade do grupo e deixa a contagem explicita.
 */
export const rotuloGrupo = (g: {
  grupo?: string | null;
  raiz_cnpj?: string | null;
  empresas?: unknown[];
  multi_estabelecimento?: boolean;
}): string => {
  const n = (g.empresas ?? []).length;
  if (!g.multi_estabelecimento) return g.grupo ?? g.raiz_cnpj ?? '—';
  const raiz = g.raiz_cnpj ? `CNPJ ${g.raiz_cnpj}` : 'grupo';
  return `${raiz} · ${n} estabelecimentos`;
};
